import ObjectUtil from "../../util/ObjectUtil"
import { IStorage } from "../../storage/Storage"
import { WORKSPACE_EVALUATION_STORAGE_KEY } from "../../../../config"
import { WorkspaceEvaluationContextDto } from "./model/dto"
import { WorkspaceEvaluationContext } from "./WorkspaceEvaluationContext"
import { Sdk } from "../../model/model"
import Logger from "../../logger"

interface VersionedWorkspaceEvaluationContextsDto {
  contexts: WorkspaceEvaluationContextDto[]
  version: string
}

export interface WorkspaceEvaluationStorage {
  get(): WorkspaceEvaluationContext[]

  set(contexts: WorkspaceEvaluationContext[]): void
}

/**
 * Persists cached contexts as a single JSON blob. The injected storage carries the workspace
 * scoping (e.g. `KeyTransformStorage.postfix(WindowLocalStorage, "_" + sdkKey)`); corrupt data is
 * dropped rather than thrown.
 *
 * The blob is stamped with the sdk version. A version mismatch drops the cache instead of feeding
 * a payload written by another sdk version into the current models.
 */
export class BrowserWorkspaceEvaluationStorage implements WorkspaceEvaluationStorage {
  constructor(private readonly sdk: Sdk, private readonly storage: IStorage) {}

  get(): WorkspaceEvaluationContext[] {
    const json = this.storage.getItem(WORKSPACE_EVALUATION_STORAGE_KEY)
    if (ObjectUtil.isNullOrUndefined(json)) {
      return []
    }
    try {
      const record: VersionedWorkspaceEvaluationContextsDto = JSON.parse(json)
      if (this.sdk.version !== record.version) {
        BrowserWorkspaceEvaluationStorage.clear(this.storage)
        return []
      }
      return record.contexts.map((context) => WorkspaceEvaluationContext.from(context))
    } catch (e) {
      Logger.log.error(`Failed to parse workspace evaluation contexts: ${e}`)
      BrowserWorkspaceEvaluationStorage.clear(this.storage)
      return []
    }
  }

  set(contexts: WorkspaceEvaluationContext[]): void {
    const record: VersionedWorkspaceEvaluationContextsDto = {
      contexts: contexts.map((context) => context.toDto()),
      version: this.sdk.version
    }
    this.storage.setItem(WORKSPACE_EVALUATION_STORAGE_KEY, JSON.stringify(record))
  }

  static clear(storage: IStorage): void {
    storage.removeItem(WORKSPACE_EVALUATION_STORAGE_KEY)
  }
}
